import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { createToggleRow, safeStorage } from '@learnforge/demo-kit';
import {
  type Polynomial,
  type Expr,
  Fraction,
  parsePolynomial,
  factor,
  canonicalise,
  parse,
  format,
  variable,
  add,
  num,
} from '../algebra';
import type { AlgebraContext, AlgebraKey } from '../types';
import { createAlgebraTiles, type AlgebraTile } from '../render/algebra-tiles';
import { createSvgEl } from '@learnforge/demo-render';
import { mountActionBar } from '../action-bar';
import { FACTOR_PRESETS } from '../data/factor-presets';

// ============================================================================
// Pure Helper Functions (exported and tested)
// ============================================================================

/**
 * Calculate the signed coefficient counts for each tile type.
 * For a polynomial a₂x² + a₁x + a₀:
 * - x2: coefficient of x² (a₂)
 * - x: coefficient of x (a₁)
 * - unit: constant term (a₀)
 * Each as a number (via .toNumber()).
 */
export function tilesNeeded(poly: Polynomial): { x2: number; x: number; unit: number } {
  const x2Coeff = poly.coeffs.length > 2 ? poly.coeffs[2]! : Fraction.ZERO;
  const xCoeff = poly.coeffs.length > 1 ? poly.coeffs[1]! : Fraction.ZERO;
  const unitCoeff = poly.coeffs.length > 0 ? poly.coeffs[0]! : Fraction.ZERO;

  return {
    x2: x2Coeff.toNumber(),
    x: xCoeff.toNumber(),
    unit: unitCoeff.toNumber(),
  };
}

/**
 * Detect special product form of a polynomial.
 * - difference-of-squares: degree 2, form [−b², 0, a²] where both are perfect squares
 * - perfect-square: degree 2, form [a², ±2ab, b²]
 * - sum-of-cubes: degree 3, form [b³, 0, 0, a³] with b³ > 0
 * - difference-of-cubes: degree 3, form [−b³, 0, 0, a³] with b³ > 0
 * - null: none of the above
 */
export function specialProductForm(
  poly: Polynomial,
): 'difference-of-squares' | 'sum-of-cubes' | 'difference-of-cubes' | 'perfect-square' | null {
  if (poly.degree === 2 && poly.coeffs.length === 3) {
    const [c0, c1, c2] = [poly.coeffs[0]!, poly.coeffs[1]!, poly.coeffs[2]!];

    // Check for difference of squares: c1 = 0, c0 < 0, c2 and |c0| perfect squares
    if (
      c1.num === 0 &&
      c0.sign < 0 &&
      isPerfectSquareRational(c2) &&
      isPerfectSquareRational(c0.abs)
    ) {
      return 'difference-of-squares';
    }

    // Check for perfect square: c2 and c0 both perfect squares, c1 = ±2ab
    if (isPerfectSquareRational(c2) && isPerfectSquareRational(c0.abs)) {
      const a = new Fraction(integerSquareRoot(c2.num), integerSquareRoot(c2.den));
      const b = new Fraction(integerSquareRoot(Math.abs(c0.num)), integerSquareRoot(c0.den));
      const twoAb = a.mul(b).mul(new Fraction(2, 1));
      if (c1.equals(twoAb) || c1.equals(twoAb.neg())) {
        return 'perfect-square';
      }
    }
  }

  if (poly.degree === 3 && poly.coeffs.length === 4) {
    const [c0, c1, c2, c3] = [poly.coeffs[0]!, poly.coeffs[1]!, poly.coeffs[2]!, poly.coeffs[3]!];

    // Check for sum/difference of cubes: c1 = 0, c2 = 0, c0 and c3 perfect cubes
    if (c1.num === 0 && c2.num === 0 && isPerfectCubeRational(c3) && isPerfectCubeRational(c0)) {
      if (c0.sign > 0) {
        return 'sum-of-cubes';
      } else {
        return 'difference-of-cubes';
      }
    }
  }

  return null;
}

/**
 * Factor a polynomial over ℚ using the factor function.
 * Returns the full FactorResult.
 */
export function factorOverQ(poly: Polynomial): ReturnType<typeof factor> {
  return factor(poly);
}

const SUPERSCRIPT_DIGITS: { [key: string]: string } = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
};

function toSuperscript(n: number): string {
  return String(n)
    .split('')
    .map((ch) => SUPERSCRIPT_DIGITS[ch] ?? ch)
    .join('');
}

/**
 * Build a human-readable factorisation headline for a polynomial, e.g.
 * "x² + 5x + 6 = (x + 2)(x + 3)". Falls back to an "irreducible" line when
 * the factor result is just the polynomial itself (no non-trivial split).
 */
export function formatFactorisation(
  poly: Polynomial,
  res: ReturnType<typeof factor>,
  irreducibleLabel: string = 'irreducible over ℚ',
): string {
  const lhs = format(poly.toExpr(), { useUnicodeSuperscripts: true });

  const isIrreducible =
    res.factors.length === 1 &&
    res.factors[0]!.multiplicity === 1 &&
    res.factors[0]!.factor.degree >= 2 &&
    res.content.equals(Fraction.ONE);

  if (isIrreducible) {
    return `${lhs} — ${irreducibleLabel}`;
  }

  let rhs = '';
  if (!res.content.equals(Fraction.ONE)) {
    rhs += format(num(res.content), { useUnicodeSuperscripts: true });
  }
  for (const f of res.factors) {
    const factorStr = format(f.factor.toExpr(), { useUnicodeSuperscripts: true });
    const exponent = f.multiplicity > 1 ? toSuperscript(f.multiplicity) : '';
    rhs += `(${factorStr})${exponent}`;
  }

  return `${lhs} = ${rhs}`;
}

/**
 * The single predicate for "can we draw the rectangle arrangement" — shared
 * by the tile layout and the side labels so they never disagree. Returns the
 * two linear-factor offsets (repeated per multiplicity) iff: the polynomial
 * is monic, there are exactly two offsets, and every offset is a non-negative
 * integer no greater than 8 (the workspace's practical tile budget).
 */
export function rectangleOffsets(
  poly: Polynomial,
  res: ReturnType<typeof factor>,
): [number, number] | null {
  const x2Coeff = poly.coeffs.length > 2 ? poly.coeffs[2]! : Fraction.ZERO;
  if (!x2Coeff.equals(Fraction.ONE)) {
    return null;
  }

  const offsets: number[] = [];
  for (const f of res.factors) {
    if (f.factor.degree === 1) {
      const lead = f.factor.coeffs[1]!;
      const off = f.factor.coeffs[0]!.div(lead).toNumber();
      for (let m = 0; m < f.multiplicity; m += 1) {
        offsets.push(off);
      }
    }
  }

  if (offsets.length !== 2) {
    return null;
  }
  if (!offsets.every((o) => Number.isInteger(o) && o >= 0 && o <= 8)) {
    return null;
  }

  return [offsets[0]!, offsets[1]!];
}

/**
 * Check if a tile arrangement forms a solid rectangle.
 * Reuses the logic from algebra-tiles: all cells in the bounding box must be
 * covered exactly once.
 */
export function isValidArrangement(tiles: readonly AlgebraTile[]): boolean {
  if (tiles.length === 0) {
    return false;
  }

  // Grid cell size is 20px, tile dimensions are in cell counts
  const getTileDimensions = (kind: string): { cols: number; rows: number } => {
    switch (kind) {
      case 'unit':
        return { cols: 1, rows: 1 };
      case 'x':
        return { cols: 3, rows: 1 };
      case 'x2':
        return { cols: 3, rows: 3 };
      default:
        return { cols: 0, rows: 0 };
    }
  };

  const getTileBounds = (
    tile: AlgebraTile,
  ): { minCol: number; maxCol: number; minRow: number; maxRow: number } => {
    const dims = getTileDimensions(tile.kind);
    return {
      minCol: tile.position.col,
      maxCol: tile.position.col + dims.cols - 1,
      minRow: tile.position.row,
      maxRow: tile.position.row + dims.rows - 1,
    };
  };

  // Compute bounding box
  let minCol = Infinity;
  let maxCol = -Infinity;
  let minRow = Infinity;
  let maxRow = -Infinity;

  const tileBounds = tiles.map((tile) => getTileBounds(tile));

  for (const bounds of tileBounds) {
    minCol = Math.min(minCol, bounds.minCol);
    maxCol = Math.max(maxCol, bounds.maxCol);
    minRow = Math.min(minRow, bounds.minRow);
    maxRow = Math.max(maxRow, bounds.maxRow);
  }

  const boundingWidth = maxCol - minCol + 1;
  const boundingHeight = maxRow - minRow + 1;

  // Count cell coverage
  const cellCoverage = Array.from({ length: boundingHeight }, () =>
    Array.from({ length: boundingWidth }, () => 0),
  );

  for (const tile of tiles) {
    const dims = getTileDimensions(tile.kind);
    for (let r = 0; r < dims.rows; r++) {
      for (let c = 0; c < dims.cols; c++) {
        const cellRow = tile.position.row - minRow + r;
        const cellCol = tile.position.col - minCol + c;
        if (cellRow < 0 || cellRow >= boundingHeight || cellCol < 0 || cellCol >= boundingWidth) {
          return false;
        }
        cellCoverage[cellRow]![cellCol]! += 1;
      }
    }
  }

  // Check every cell is covered exactly once
  for (let r = 0; r < boundingHeight; r++) {
    for (let c = 0; c < boundingWidth; c++) {
      if (cellCoverage[r]![c] !== 1) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Completing the square for x² + bx + c:
 * Returns { added, subtracted, result }
 * - added = (b/2)² − c (the amount added to the constant to reach (b/2)²)
 * - subtracted = same value
 * - result = (x + b/2)² − added (as an Expr)
 */
export function completeTheSquareStep(poly: Polynomial): {
  added: Fraction;
  subtracted: Fraction;
  result: Expr;
} {
  // Expect degree 2: x² + bx + c
  if (poly.degree !== 2 || poly.coeffs.length !== 3) {
    throw new Error('completeTheSquareStep: polynomial must be quadratic');
  }

  const c0 = poly.coeffs[0]!;
  const c1 = poly.coeffs[1]!;
  const c2 = poly.coeffs[2]!;

  // Normalize by leading coefficient (assume monic: c2 = 1)
  if (!c2.equals(Fraction.ONE)) {
    throw new Error('completeTheSquareStep: leading coefficient must be 1');
  }

  // b = c1, so (b/2)² = (c1/2)²
  const bHalf = c1.mul(new Fraction(1, 2));
  const bHalfSquared = bHalf.mul(bHalf);

  // added = (b/2)² − c
  const added = bHalfSquared.sub(c0);

  // result = (x + b/2)² − added
  // Construct (x + b/2)² − added
  const { num, den } = bHalf;
  const bHalfStr = den === 1 ? `${num}` : `${num}/${den}`;
  const xPlusBHalf = parse(`(x + ${bHalfStr})`, { variableSet: new Set(['x']) });
  const xPlusBHalfSquared = {
    kind: 'pow' as const,
    base: xPlusBHalf,
    exp: { kind: 'num' as const, value: Fraction.from(2) },
  };
  const result = {
    kind: 'add' as const,
    children: [xPlusBHalfSquared, { kind: 'num' as const, value: added.neg() }],
  };

  return {
    added,
    subtracted: added,
    result: canonicalise(result),
  };
}

// ============================================================================
// Helper functions for perfect square and cube detection
// ============================================================================

function isPerfectSquareRational(frac: Fraction): boolean {
  if (frac.num <= 0) return false;
  return isPerfectSquareInteger(frac.num) && isPerfectSquareInteger(frac.den);
}

function isPerfectSquareInteger(n: number): boolean {
  if (n < 0) return false;
  const root = Math.sqrt(n);
  return Number.isInteger(root);
}

function isPerfectCubeRational(frac: Fraction): boolean {
  if (frac.num === 0) return true;
  const absNum = Math.abs(frac.num);
  return isPerfectCubeInteger(absNum) && isPerfectCubeInteger(frac.den);
}

function isPerfectCubeInteger(n: number): boolean {
  if (n === 0) return true;
  const absN = Math.abs(n);
  const root = Math.round(Math.cbrt(absN));
  return root * root * root === absN;
}

function integerSquareRoot(n: number): number {
  if (n < 0) throw new Error('integerSquareRoot: negative input');
  return Math.floor(Math.sqrt(n) + 0.5);
}

// ============================================================================
// State
// ============================================================================

interface State {
  presetId: string;
  advancedMode: boolean;
  polynomial: string;
}

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): CustomExperimentSpec<State, AlgebraKey, AlgebraContext> {
  return {
    labelKey: 'experiment.factor-tiles.label',
    demoId: 'algebra',
    advancedKey: 'factor-tiles',

    initialState(ctx): State {
      const defaultPreset = FACTOR_PRESETS[0]!;
      return {
        presetId: defaultPreset.id,
        advancedMode: ctx.advanced,
        polynomial: defaultPreset.polynomial,
      };
    },

    applyPreset(state, presetId): State {
      const preset = FACTOR_PRESETS.find((p) => p.id === presetId);
      if (!preset) return state;
      return {
        ...state,
        presetId: preset.id,
        polynomial: preset.polynomial,
      };
    },

    createRender(ctx, host, controls) {
      const ADVANCED_STORAGE_KEY = 'algebra.factor-tiles.advanced';

      // ── DOM: SVG stage ──
      const svg = createSvgEl('svg', { viewBox: '0 0 800 460', class: 'alg-factor-tiles-svg' });
      host.appendChild(svg);

      // Create wrapper group for tiles (for centering/scaling)
      const tilesGroup = createSvgEl('g', { id: 'tiles-wrapper' });
      svg.appendChild(tilesGroup);

      // Initialise with first preset so createAlgebraTiles has something to render.
      // update() will immediately reconcile from state on first call.
      const initPreset = FACTOR_PRESETS[0]!;
      const initPoly = parsePolynomial(initPreset.polynomial, 'x');
      const initNeeded = tilesNeeded(initPoly);
      const initTiles: AlgebraTile[] = [];
      if (initNeeded.x2 !== 0) {
        initTiles.push({
          id: 'x2-0',
          kind: 'x2',
          sign: initNeeded.x2 > 0 ? 1 : -1,
          position: { col: 0, row: 0 },
          orientation: 'horizontal',
        });
      }

      const tilesHandle = createAlgebraTiles(tilesGroup, {
        tiles: initTiles,
        workspaceCols: 40,
        workspaceRows: 23,
        width: 800,
        height: 460,
      });

      // Group for side labels
      const labelsGroup = createSvgEl('g', { id: 'tiles-labels' });
      svg.appendChild(labelsGroup);

      // ── DOM: Panel ──

      // Polynomial input row
      const polyInputRow = document.createElement('div');
      polyInputRow.className = 'alg-panel-row';
      const polyLabel = document.createElement('label');
      polyLabel.textContent = ctx.i18n.t('panel.enter-number');
      const polyInput = document.createElement('input');
      polyInput.type = 'text';
      polyInput.className = 'alg-text-input';
      polyInput.value = initPreset.polynomial;
      polyInput.addEventListener('change', () => {
        try {
          // Validate that the polynomial parses before accepting it
          parsePolynomial(polyInput.value, 'x');
          controls.setState((s) => ({ ...s, polynomial: polyInput.value }));
        } catch (_e) {
          // Silently revert on parse error — restore from state
          controls.setState((s) => s);
        }
      });
      polyInputRow.appendChild(polyLabel);
      polyInputRow.appendChild(polyInput);
      ctx.panel.appendChild(polyInputRow);

      // Advanced toggle
      const advancedToggle = createToggleRow({
        labelKey: 'btn.advanced',
        i18n: ctx.i18n,
        checked: ctx.advanced,
        onChange: (v) => {
          safeStorage.set(ADVANCED_STORAGE_KEY, v ? 'true' : 'false');
          controls.setState((s) => {
            let nextPresetId = s.presetId;
            let nextPolynomial = s.polynomial;
            if (v) {
              const firstAdvanced = FACTOR_PRESETS.find((p) => p.advancedOnly);
              if (firstAdvanced) {
                nextPresetId = firstAdvanced.id;
                nextPolynomial = firstAdvanced.polynomial;
              }
            } else {
              const firstFoundation = FACTOR_PRESETS.find((p) => !p.advancedOnly);
              if (firstFoundation) {
                nextPresetId = firstFoundation.id;
                nextPolynomial = firstFoundation.polynomial;
              }
            }
            return { ...s, advancedMode: v, presetId: nextPresetId, polynomial: nextPolynomial };
          });
        },
      });
      ctx.panel.appendChild(advancedToggle.element);

      // ── DOM: Action bar ──
      const actionBar = mountActionBar(ctx.actionBar, ctx.i18n);

      // ── RenderHandle ──
      let lastAdvancedMode: boolean | undefined;

      const update = (state: State): void => {
        const { polynomial, advancedMode } = state;

        // --- Derive model from state ---
        let currentPoly: Polynomial;
        try {
          currentPoly = parsePolynomial(polynomial, 'x');
        } catch (_e) {
          // Fallback to first preset polynomial if state polynomial is somehow invalid
          currentPoly = parsePolynomial(FACTOR_PRESETS[0]!.polynomial, 'x');
        }

        // Rebuild tiles from the polynomial
        const tiles: AlgebraTile[] = [];
        let tileId = 0;
        const needed = tilesNeeded(currentPoly);
        const factorResult = factorOverQ(currentPoly);
        const special = specialProductForm(currentPoly);
        const offsets = rectangleOffsets(currentPoly, factorResult);

        if (offsets !== null) {
          const x2Count = Math.abs(needed.x2);
          const [a, b] = offsets;

          for (let i = 0; i < x2Count; i++) {
            tiles.push({
              id: `x2-${tileId++}`,
              kind: 'x2',
              sign: needed.x2 > 0 ? 1 : -1,
              position: { col: 0, row: 0 },
              orientation: 'horizontal',
            });
          }

          for (let i = 0; i < a; i += 1) {
            tiles.push({
              id: `x-v-${tileId++}`,
              kind: 'x',
              sign: needed.x > 0 ? 1 : -1,
              position: { col: 3 + i, row: 0 },
              orientation: 'vertical',
            });
          }
          for (let i = 0; i < b; i += 1) {
            tiles.push({
              id: `x-h-${tileId++}`,
              kind: 'x',
              sign: needed.x > 0 ? 1 : -1,
              position: { col: 0, row: 3 + i },
              orientation: 'horizontal',
            });
          }
          for (let i = 0; i < a; i += 1) {
            for (let j = 0; j < b; j += 1) {
              tiles.push({
                id: `unit-${tileId++}`,
                kind: 'unit',
                sign: needed.unit > 0 ? 1 : -1,
                position: { col: 3 + i, row: 3 + j },
                orientation: 'horizontal',
              });
            }
          }
        } else {
          if (needed.x2 !== 0) {
            for (let i = 0; i < Math.abs(needed.x2); i++) {
              tiles.push({
                id: `x2-${tileId++}`,
                kind: 'x2',
                sign: needed.x2 > 0 ? 1 : -1,
                position: { col: i * 4, row: 0 },
                orientation: 'horizontal',
              });
            }
          }

          if (needed.x !== 0) {
            for (let i = 0; i < Math.abs(needed.x); i++) {
              tiles.push({
                id: `x-${tileId++}`,
                kind: 'x',
                sign: needed.x > 0 ? 1 : -1,
                position: { col: 0, row: 5 + i * 2 },
                orientation: 'horizontal',
              });
            }
          }

          if (needed.unit !== 0) {
            for (let i = 0; i < Math.abs(needed.unit); i++) {
              tiles.push({
                id: `unit-${tileId++}`,
                kind: 'unit',
                sign: needed.unit > 0 ? 1 : -1,
                position: { col: 8 + (i % 4), row: 5 + Math.floor(i / 4) * 2 },
                orientation: 'horizontal',
              });
            }
          }
        }

        // --- Update tiles render ---
        tilesHandle.update({ tiles });

        // --- Update tiles display: centre + scale bounding box, rebuild labels ---
        const CELL = 20;
        const cellDims = (kind: string, orientation: string): { cols: number; rows: number } => {
          if (kind === 'unit') return { cols: 1, rows: 1 };
          if (kind === 'x') {
            return orientation === 'vertical' ? { cols: 1, rows: 3 } : { cols: 3, rows: 1 };
          }
          return { cols: 3, rows: 3 };
        };
        let maxCol = 1;
        let maxRow = 1;
        for (const t of tiles) {
          const d = cellDims(t.kind, t.orientation);
          maxCol = Math.max(maxCol, t.position.col + d.cols);
          maxRow = Math.max(maxRow, t.position.row + d.rows);
        }
        const bboxW = maxCol * CELL;
        const bboxH = maxRow * CELL;
        const scale = Math.min(330 / bboxH, 520 / bboxW, 3.2);
        const scaledW = bboxW * scale;
        const scaledH = bboxH * scale;
        const translateX = (800 - scaledW) / 2 + 30;
        const translateY = (460 - scaledH) / 2 + 30;

        tilesGroup.setAttribute(
          'transform',
          `translate(${translateX}, ${translateY}) scale(${scale})`,
        );

        labelsGroup.replaceChildren();

        // Headline: always-present factorisation statement, plus a soft
        // sub-line naming the special product form when one applies.
        const headlineEl = createSvgEl('text', {
          x: 400,
          y: 28,
          class: 'alg-title',
          'text-anchor': 'middle',
        });
        headlineEl.textContent = formatFactorisation(
          currentPoly,
          factorResult,
          ctx.i18n.t('factor.irreducible'),
        );
        labelsGroup.appendChild(headlineEl);

        if (special !== null) {
          const specialEl = createSvgEl('text', {
            x: 400,
            y: 50,
            class: 'alg-text-soft',
            'text-anchor': 'middle',
          });
          specialEl.textContent = ctx.i18n.t(`special.${special}`);
          labelsGroup.appendChild(specialEl);
        }

        // Side labels: same predicate as the rectangle arrangement — they
        // only appear when the rectangle draws.
        if (offsets !== null) {
          const [a, b] = offsets;

          const topLabelEl = createSvgEl('text', {
            x: translateX + scaledW / 2,
            y: translateY - 14,
            class: 'alg-title',
            'text-anchor': 'middle',
          });
          topLabelEl.textContent = format(add(variable('x'), num(new Fraction(a, 1))), {
            useUnicodeSuperscripts: true,
          });
          labelsGroup.appendChild(topLabelEl);

          const leftLabelEl = createSvgEl('text', {
            x: translateX - 14,
            y: translateY + scaledH / 2,
            class: 'alg-title',
            'text-anchor': 'end',
            'dominant-baseline': 'middle',
          });
          leftLabelEl.textContent = format(add(variable('x'), num(new Fraction(b, 1))), {
            useUnicodeSuperscripts: true,
          });
          labelsGroup.appendChild(leftLabelEl);
        }

        // --- Sync poly input (GUIDANCE B: guard to avoid clobbering caret) ---
        if (polyInput.value !== polynomial) {
          polyInput.value = polynomial;
        }

        // --- Action bar (GUIDANCE A: only rebuild when advancedMode changes) ---
        if (advancedMode !== lastAdvancedMode) {
          lastAdvancedMode = advancedMode;
          const availablePresets = advancedMode
            ? FACTOR_PRESETS
            : FACTOR_PRESETS.filter((p) => !p.advancedOnly);

          actionBar.setActions([
            {
              id: 'reset',
              labelKey: 'btn.reset',
              onClick: () => {
                controls.setState((s) => {
                  const preset = FACTOR_PRESETS.find((p) => p.id === s.presetId);
                  if (!preset) return s;
                  return { ...s, polynomial: preset.polynomial };
                });
              },
            },
            {
              id: 'randomise',
              labelKey: 'btn.randomise',
              onClick: () => {
                const idx = Math.floor(ctx.prng() * availablePresets.length);
                const preset = availablePresets[idx]!;
                controls.setState((s) => ({
                  ...s,
                  presetId: preset.id,
                  polynomial: preset.polynomial,
                }));
              },
            },
          ]);
        }
      };

      return {
        update,
        dispose() {
          // GUIDANCE C: tear down only render-owned resources; harness clears host/panel/actionBar
          tilesHandle.destroy();
          actionBar.clear();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());
